"use client";

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface LibraryPreviewProps {
  items: Array<{ id: string; url: string; title: string }>;
  initialIndex: number;
  onClose: () => void;
}

export function LibraryPreview({ items, initialIndex, onClose }: LibraryPreviewProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setCurrentIndex(prev => (prev > 0 ? prev - 1 : items.length - 1));
    } else if (e.key === 'ArrowRight') {
      setCurrentIndex(prev => (prev < items.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [items.length, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 max-w-4xl w-full mx-4">
        <div className="flex justify-end mb-2">
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="relative aspect-video">
          <Image
            src={items[currentIndex].url}
            alt={items[currentIndex].title}
            fill
            className="object-contain"
          />
        </div>
        
        <div className="flex items-center justify-between mt-4">
          <button 
            onClick={() => setCurrentIndex(prev => (prev > 0 ? prev - 1 : items.length - 1))}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {currentIndex + 1} / {items.length}
          </span>
          
          <button 
            onClick={() => setCurrentIndex(prev => (prev < items.length - 1 ? prev + 1 : 0))}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
}